/**
 * Create a spread-over rule, or change one — a change is a new version: rules are
 * versioned with who, when and why (Journey F). Used by Ledger rules ›
 * Spread over. The rule is the purchase
 * ledger's, written to the URL the rules read carried; the ledger refuses a
 * missing reason or an unknown subject, and those errors are bound to their
 * inputs here.
 */

import { useCallback, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  SpreadOverRule,
  SpreadOverRuleScope,
} from "@shared-core/data/accruals-and-prepayments-types";
import { accrualsErrorMessage } from "@shared-core/data/api/accruals-and-prepayments";
import { extractFieldErrors } from "@shared-core/data/validation";
import {
  SPREAD_OVER_WORDS,
  spreadOverAnswer,
} from "@shared-core/domain/spread-over";
import {
  EntityCombobox,
  type EntityOption,
} from "../../patterns/EntityCombobox";
import { Button } from "../../primitives/button";
import { Checkbox } from "../../primitives/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { Label } from "../../primitives/label";
import { RadioGroup, RadioGroupItem } from "../../primitives/radio-group";
import { Textarea } from "../../primitives/textarea";

export type SpreadOverRuleTarget =
  /** A new rule, for a supplier unless `scope` says a nominal. */
  | { mode: "create"; scope?: SpreadOverRuleScope }
  | { mode: "edit"; rule: SpreadOverRule };

/** "485 — Subscriptions" for a nominal, the name for a supplier. */
export function spreadOverRuleSubjectName(
  rule: Pick<SpreadOverRule, "scope" | "subjectKey" | "subjectLabel">,
): string {
  return rule.scope === "nominal"
    ? `${rule.subjectKey} — ${rule.subjectLabel}`
    : rule.subjectLabel;
}

export function SpreadOverRuleDialog({
  target,
  spreadOverRulesUrl,
  onCancel,
  onSaved,
}: {
  target: SpreadOverRuleTarget;
  /** Where a new version is posted: the URL the rules read carried. */
  spreadOverRulesUrl: string;
  onCancel: () => void;
  /** After the ledger has saved it, with what to tell the reviewer. */
  onSaved: (message: string) => void;
}) {
  const adapter = useDataAdapter();
  const isEdit = target.mode === "edit";
  const [scope, setScope] = useState<SpreadOverRuleScope>(
    isEdit ? target.rule.scope : (target.scope ?? "supplier"),
  );
  // Changing a rule fixes its subject; a new one picks it below.
  const fixedSubject = isEdit
    ? {
        key: target.rule.subjectKey,
        label: spreadOverRuleSubjectName(target.rule),
      }
    : null;
  const [subject, setSubject] = useState<EntityOption | null>(null);
  const [isSpreadOver, setIsSpreadOver] = useState(
    isEdit ? target.rule.current.isSpreadOver : true,
  );
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState<{
    subject?: string;
    reason?: string;
    form?: string;
  }>({});
  const [isSaving, setIsSaving] = useState(false);

  const fetchSubjects = useCallback(
    async (q: string): Promise<EntityOption[]> => {
      const subjects = await adapter.searchSpreadOverRuleSubjects({ scope, q });
      return subjects.map((s) => ({
        id: s.key,
        label: scope === "nominal" ? `${s.key} — ${s.label}` : s.label,
      }));
    },
    [adapter, scope],
  );

  const subjectName = fixedSubject?.label ?? subject?.label ?? "";
  // The ledger records nothing for a write that changes nothing.
  const isUnchanged =
    isEdit && isSpreadOver === target.rule.current.isSpreadOver;

  const submit = async () => {
    const subjectKey = fixedSubject?.key ?? subject?.id;
    const next: typeof errors = {};
    if (!subjectKey) next.subject = `Choose the ${scope} this rule applies to.`;
    if (!reason.trim())
      next.reason =
        "Why lines from here are or are not spread — the rule's audit trail.";
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setIsSaving(true);
    try {
      await adapter.writeSpreadOverRule(spreadOverRulesUrl, {
        scope,
        subjectKey: subjectKey!,
        isSpreadOver,
        reason: reason.trim(),
      });
      onSaved(
        `New purchase invoice lines ${scope === "supplier" ? "from" : "on"} ${subjectName} will be captured as ${spreadOverAnswer(isSpreadOver).toLowerCase()}.`,
      );
    } catch (err) {
      const fields = extractFieldErrors(err);
      const byField = Object.fromEntries(
        fields.map((f) => [f.field, f.message]),
      );
      setErrors({
        subject: byField.subject_key,
        reason: byField.reason,
        form:
          fields.length === 0
            ? accrualsErrorMessage(err, "The rule was not saved.")
            : undefined,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? `Change the spread-over rule for ${subjectName}`
              : "New spread-over rule"}
          </DialogTitle>
          <DialogDescription>
            Applied to each purchase invoice line when its purchase invoice is
            captured in Purchase invoices; the reviewer can still change a
            line's {SPREAD_OVER_WORDS.name} in Amend details…, with a reason.
            Changing a rule does not change purchase invoices already confirmed.
          </DialogDescription>
        </DialogHeader>
        {errors.form && (
          <p role="alert" className="text-sm text-red-600">
            {errors.form}
          </p>
        )}
        <div className="grid gap-4">
          {!fixedSubject && (
            <>
              <div className="space-y-1.5">
                <Label>Applies to</Label>
                <RadioGroup
                  value={scope}
                  onValueChange={(v) => {
                    setScope(v as SpreadOverRuleScope);
                    setSubject(null);
                  }}
                  className="flex gap-4"
                >
                  <label className="flex items-center gap-2 text-sm">
                    <RadioGroupItem value="supplier" /> A supplier
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <RadioGroupItem value="nominal" /> A nominal
                  </label>
                </RadioGroup>
              </div>
              <div className="space-y-1.5">
                <Label>{scope === "supplier" ? "Supplier" : "Nominal"}</Label>
                <EntityCombobox
                  key={scope}
                  value={subject}
                  onChange={setSubject}
                  fetchOptions={fetchSubjects}
                  placeholder={
                    scope === "supplier"
                      ? "Search suppliers with no rule"
                      : "Search accounts with no rule"
                  }
                  ariaLabel={scope === "supplier" ? "Supplier" : "Nominal"}
                  aria-invalid={Boolean(errors.subject)}
                  aria-describedby={
                    errors.subject ? "rule-subject-error" : undefined
                  }
                />
                {errors.subject && (
                  <p id="rule-subject-error" className="text-sm text-red-600">
                    {errors.subject}
                  </p>
                )}
              </div>
            </>
          )}
          <label className="flex items-start gap-2 text-sm">
            <Checkbox
              checked={isSpreadOver}
              onCheckedChange={(c) => setIsSpreadOver(c === true)}
              className="mt-0.5"
              aria-label={SPREAD_OVER_WORDS.tick}
            />
            <span>
              <span className="font-medium">{SPREAD_OVER_WORDS.tick}</span>
              <span className="text-muted-foreground block text-xs">
                Ticked: the line codes to 1103 Prepayments and releases over the
                period it covers. Unticked: expensed in the invoice month.
              </span>
            </span>
          </label>
          {isUnchanged && (
            <p className="text-muted-foreground text-xs">
              The rule already says{" "}
              {spreadOverAnswer(isSpreadOver).toLowerCase()}: change the tick to
              record a new version.
            </p>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="rule-reason">Reason</Label>
            <Textarea
              id="rule-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              aria-invalid={Boolean(errors.reason)}
              aria-describedby={errors.reason ? "rule-reason-error" : undefined}
            />
            {errors.reason && (
              <p id="rule-reason-error" className="text-sm text-red-600">
                {errors.reason}
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={isSaving || isUnchanged}>
            {isSaving ? "Saving…" : isEdit ? "Save new version" : "Create rule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
